'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'

import dynamic from 'next/dynamic'

import { FullContainerSpinner } from '@/components/common/Spinner'
import type { RemoteSpreadsheetViewerProps } from '@/components/office/SpreadsheetViewer/RemoteSpreadsheetViewer'
import type { SpreadsheetSelectionDescriptor } from '@/components/spreadsheet/Spreadsheet/types'

import type { File } from '@/types/File'

const RemoteSpreadsheetViewer = dynamic(
  () =>
    import('@/components/office/SpreadsheetViewer/RemoteSpreadsheetViewer').then(
      (m) => m.RemoteSpreadsheetViewer,
    ),
  { ssr: false, loading: () => <FullContainerSpinner /> },
)

interface SpreadsheetPreviewHostProps {
  file: File
  pageScale: number
  pageScaleMode: RemoteSpreadsheetViewerProps['pageScaleMode']
  /** Bumped when the surrounding chat consumes the viewer selection
   * (add-to-chat): clears the range-selection overlay. */
  selectionResetNonce?: number
  /** Spreadsheet range to select on open (a selection card being re-opened);
   * re-applied whenever the key changes. */
  initialSelection?: SpreadsheetSelectionDescriptor | null
  initialSelectionKey?: number
  onAutoFitPageScaleChange: (pageScale: number) => void
  /** The shared native-preview load path; spreadsheets report one page. */
  onLoadSuccess: (pageCount: number) => void
  onLoadError: RemoteSpreadsheetViewerProps['onLoadError']
  onRenderStart: () => void
  onViewportStateChange: RemoteSpreadsheetViewerProps['onViewportStateChange']
}

/**
 * Spreadsheet branch of the full-screen viewer. Owns the spreadsheet-only
 * controlled selection (and its seed/reset plumbing) so selection churn
 * re-renders this leaf, not the shared viewer chrome.
 */
export const SpreadsheetPreviewHost: React.FC<SpreadsheetPreviewHostProps> = ({
  file,
  pageScale,
  pageScaleMode,
  selectionResetNonce,
  initialSelection,
  initialSelectionKey,
  onAutoFitPageScaleChange,
  onLoadSuccess,
  onLoadError,
  onRenderStart,
  onViewportStateChange,
}) => {
  // The viewer owns its in-viewer selection overlay; hold that selection
  // controlled-after-first-change so a selectionResetNonce bump (add-to-chat
  // consumed it) can clear the overlay. `undefined` means never selected:
  // the viewer stays uncontrolled.
  const [selection, setSelection] = useState<
    SpreadsheetSelectionDescriptor | null | undefined
  >(undefined)
  useEffect(() => {
    setSelection(undefined)
  }, [file.id])
  // Seed from a reopened selection card. Declared after the file-change
  // reset so a same-commit file switch resets first, then seeds. Cloned
  // per application: a repeat click bumps only the key, and the fresh
  // identity is what re-runs the viewer's controlled-selection effect
  // (which also refocuses the selection's sheet).
  useEffect(() => {
    if (initialSelection) {
      setSelection({ ...initialSelection })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectionKey])
  const lastSelectionResetNonceRef = useRef(selectionResetNonce)
  useEffect(() => {
    if (
      selectionResetNonce === undefined ||
      selectionResetNonce === lastSelectionResetNonceRef.current
    ) {
      return
    }
    lastSelectionResetNonceRef.current = selectionResetNonce
    // Force-controlled null even when this instance never observed a
    // selection: a remount (expand/collapse, tab switch) resets this state to
    // `undefined` while the retained per-workbook viewer store still holds
    // the range - the mount-time viewport emit re-reports it, so the pill and
    // overlay come back without any echo ever reaching this instance. A
    // `previous === undefined` guard would make the clear a permanent no-op
    // in that state. Controlled null is idempotent when nothing is selected
    // (the viewer's apply effect bails on descriptor equality).
    setSelection(null)
  }, [selectionResetNonce])
  const handleLoadSuccess = useCallback(() => {
    onLoadSuccess(1)
  }, [onLoadSuccess])
  return (
    <RemoteSpreadsheetViewer
      file={file}
      className="h-full"
      pageScale={pageScale}
      pageScaleMode={pageScaleMode}
      onAutoFitPageScaleChange={onAutoFitPageScaleChange}
      onLoadSuccess={handleLoadSuccess}
      onLoadError={onLoadError}
      onRenderStart={onRenderStart}
      onViewportStateChange={onViewportStateChange}
      selection={selection}
      onSelectionChange={setSelection}
    />
  )
}

SpreadsheetPreviewHost.displayName = 'FullScreenViewer.SpreadsheetPreviewHost'
